// src/context/NotificationContext.jsx
// -----------------------------------------------------------------------------
// Global Notification Context. Manages fetching, polling, marking read, and
// deleting notifications for the currently logged-in user.
// -----------------------------------------------------------------------------
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import notificationService from '../api/notificationService';
import { toast } from 'react-toastify';

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const pollIntervalRef = useRef(null);

  // Helper to extract user ID reliably
  const getUserID = useCallback(() => {
    return user?.userID || user?.userId || user?.id;
  }, [user]);

  // Fetch all notifications for the user
  const fetchNotifications = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);

    try {
      const response = await notificationService.getAll();
      const list = response.data || [];
      // Sort notifications by created date descending
      list.sort((a, b) => new Date(b.createdDttm || b.CreatedDttm) - new Date(a.createdDttm || a.CreatedDttm));
      setNotifications(list);
      
      // Calculate unread count based on isReadFlag
      const unreads = list.filter(n => !(n.isReadFlag || n.IsReadFlag || n.isRead || n.IsRead));
      setUnreadCount(unreads.length);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  // Fetch only unread notifications (or count) for light polling
  const fetchUnreadCountOnly = useCallback(async () => {
    try {
      const response = await notificationService.getAll();
      const list = response.data || [];
      const unreads = list.filter(n => !(n.isReadFlag || n.IsReadFlag || n.isRead || n.IsRead));
      setUnreadCount(unreads.length);
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  }, []);

  // Mark a single notification as read
  const markAsRead = useCallback(async (notificationId) => {
    if (!notificationId) return;
    
    // Optimistic UI update
    setNotifications(prev =>
      prev.map(n => {
        const id = n.notificationId ?? n.notificationID ?? n.NotificationId ?? n.NotificationID;
        if (id === notificationId) {
          return { ...n, isReadFlag: true, IsReadFlag: true };
        }
        return n;
      })
    );
    setUnreadCount(c => Math.max(0, c - 1));

    try {
      await notificationService.markAsRead(notificationId);
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      // Revert if error occurs (refetch)
      fetchNotifications();
    }
  }, [fetchNotifications]);

  // Mark all notifications as read for current user
  const markAllAsRead = useCallback(async () => {
    const userID = getUserID();
    if (!userID) return;

    // Optimistic UI update
    setNotifications(prev =>
      prev.map(n => ({ ...n, isReadFlag: true, IsReadFlag: true }))
    );
    setUnreadCount(0);

    try {
      await notificationService.markAllAsRead(userID);
      toast.success('All notifications marked as read.');
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
      toast.error('Failed to mark all notifications as read.');
      fetchNotifications();
    }
  }, [getUserID, fetchNotifications]);

  // Delete a notification
  const deleteNotification = useCallback(async (notificationId) => {
    if (!notificationId) return;

    // Optimistic UI update
    let wasUnread = false;
    setNotifications(prev =>
      prev.filter(n => {
        const id = n.notificationId ?? n.notificationID ?? n.NotificationId ?? n.NotificationID;
        if (id === notificationId) {
          wasUnread = !(n.isReadFlag || n.IsReadFlag || n.isRead || n.IsRead);
          return false;
        }
        return true;
      })
    );
    if (wasUnread) {
      setUnreadCount(c => Math.max(0, c - 1));
    }

    try {
      await notificationService.delete(notificationId);
      toast.success('Notification deleted.');
    } catch (err) {
      console.error('Failed to delete notification:', err);
      toast.error('Failed to delete notification.');
      fetchNotifications();
    }
  }, [fetchNotifications]);

  // Effect to load data and setup polling when user changes
  useEffect(() => {
    const userID = getUserID();
    if (userID) {
      fetchNotifications(true);

      // Clear any existing poll timer
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }

      // Poll unread count every 30 seconds
      pollIntervalRef.current = setInterval(() => {
        fetchUnreadCountOnly();
      }, 30000);
    } else {
      // Clear data on logout
      setNotifications([]);
      setUnreadCount(0);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [user, fetchNotifications, fetchUnreadCountOnly, getUserID]);

  const value = {
    notifications,
    unreadCount,
    loading,
    fetchNotifications: () => fetchNotifications(false),
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return ctx;
}
